import { useParams, Link, Navigate } from "react-router-dom";
import { ChevronLeft, Hammer, Palette, Network, MapPin } from "lucide-react";
import { GROUPS, ROTATIONS } from "../data/conferenceData";

const ICON_MAP = {
  construct: Hammer,
  palette: Palette,
  network: Network,
};

export default function GroupDetail() {
  const { id } = useParams();
  const group = GROUPS.find((g) => g.id === id);
  if (!group) return <Navigate to="/group" replace />;

  const schedule = ROTATIONS[group.id] || [];
  const Icon = ICON_MAP[group.icon] || Hammer;
  const txt = group.textColor || "white";

  return (
    <div
      style={{ padding: "16px", paddingBottom: "40px" }}
      data-testid={`group-detail-${group.id}`}
    >
      {/* Back row */}
      <Link
        to="/group"
        className="flex items-center"
        style={{ marginBottom: "14px", gap: "2px" }}
        data-testid="group-back-btn"
      >
        <ChevronLeft size={20} color="var(--primary)" />
        <span
          style={{
            color: "var(--primary)",
            fontWeight: 700,
            fontSize: "15px",
          }}
        >
          All Groups
        </span>
      </Link>

      {/* Header card — flex row, icon box + label/name */}
      <div
        className="flex items-center"
        style={{
          backgroundColor: group.color,
          borderRadius: "18px",
          padding: "16px",
          marginBottom: "24px",
        }}
        data-testid="group-detail-header"
      >
        <div
          className="flex items-center justify-center"
          style={{
            width: "48px",
            height: "48px",
            borderRadius: "14px",
            backgroundColor: group.accent,
          }}
        >
          <Icon size={28} color={group.color} strokeWidth={2} />
        </div>
        <div className="flex-1" style={{ marginLeft: "12px" }}>
          <p
            style={{
              fontSize: "11px",
              fontWeight: 800,
              letterSpacing: "1.5px",
              opacity: 0.85,
              color: txt,
            }}
          >
            {group.bandLabel.toUpperCase()} · YOUR GROUP
          </p>
          <p
            style={{
              fontSize: "20px",
              fontWeight: 800,
              marginTop: "2px",
              color: txt,
            }}
          >
            {group.name}
          </p>
        </div>
      </div>

      <p
        style={{
          fontSize: "12px",
          fontWeight: 800,
          color: "var(--primary)",
          letterSpacing: "1.2px",
          marginBottom: "12px",
          textTransform: "uppercase",
        }}
      >
        Today's Rotation
      </p>

      {schedule.map((item, idx) => {
        const isLast = idx === schedule.length - 1;
        return (
          <div
            key={`${item.time}-${item.session}`}
            className="flex items-stretch"
            data-testid={`rotation-${item.time}`}
          >
            {/* Timeline column */}
            <div
              className="flex flex-col items-center"
              style={{ width: "22px", paddingTop: "16px" }}
            >
              <div
                style={{
                  width: "12px",
                  height: "12px",
                  borderRadius: "6px",
                  backgroundColor: group.color,
                }}
              />
              {!isLast && (
                <div
                  className="flex-1"
                  style={{
                    width: "2px",
                    backgroundColor: "var(--border)",
                    marginTop: "4px",
                  }}
                />
              )}
            </div>

            <div
              className="flex-1"
              style={{
                backgroundColor: "var(--surface)",
                borderRadius: "14px",
                padding: "14px",
                marginBottom: "12px",
                marginLeft: "10px",
                border: "1px solid var(--border)",
              }}
            >
              <p
                style={{
                  fontWeight: 800,
                  fontSize: "12px",
                  letterSpacing: "0.5px",
                  marginBottom: "4px",
                  color: group.color,
                }}
              >
                {item.time}
              </p>
              <p
                style={{
                  fontSize: "16px",
                  fontWeight: 700,
                  color: "var(--primary-dark)",
                  marginBottom: "6px",
                  lineHeight: "21px",
                }}
              >
                {item.session}
              </p>
              <div className="flex items-center" style={{ gap: "4px" }}>
                <MapPin size={14} color={group.color} />
                <span
                  style={{
                    color: "var(--text-muted)",
                    fontSize: "13px",
                    fontWeight: 600,
                    whiteSpace: "pre-line",
                  }}
                >
                  {item.location}
                </span>
              </div>
              {item.session === "Lunch" && (
                <p
                  style={{
                    marginTop: "8px",
                    fontSize: "13px",
                    fontWeight: 700,
                    color: "var(--primary-dark)",
                    lineHeight: "19px",
                  }}
                >
                  Dr. Phyall will be autographing his book "ACT Now" for teachers during lunch.
                </p>
              )}
              {(item.session === "Magic School AI & AI Prompting" ||
                item.session === "AI in Middle Schools") && (
                <p
                  style={{
                    marginTop: "10px",
                    fontSize: "12px",
                    fontStyle: "italic",
                    color: "var(--text-muted)",
                    lineHeight: "18px",
                  }}
                  data-testid="rotation-room-capacity-note"
                >
                  Participants may select any available session room for this time block. Once a room reaches capacity, please proceed to another listed room to ensure balanced seating and a smooth learning experience.
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
